import React, { useEffect, useRef, useState } from 'react';
import jsPDF from 'jspdf';
import { utils, write } from 'xlsx';
import './gridStyle.css';
import config from '../../config.json';

import GridColumn from './gridColumn.jsx';

import imgExcel from '../../design/icons/excel.svg'
import imgCsv from '../../design/icons/csv.svg'
import imgPdf from '../../design/icons/pdf-svgrepo-com.svg'
import imgBack from '../../design/icons/back.svg'
import imgForward from '../../design/icons/forward.svg'
import imgLoading from '../../design/icons/loading.svg'

// Recibe rowClick (column, rowData) y data: { title, data }.
// title: strings o { value, span?, type?, decimals?, exportLabel? }.
// exportLabel: texto plano para Excel/CSV/PDF cuando value es un nodo React.
// sin type, strings tipo "25.00000" se exportan como número; 'text' fuerza texto (CUIT, códigos).
// decimals: con 'number' o 'currency' (default 2) para formato Excel.
// data: { id, print[], rowData?, style? }; print puede ser primitivo o { print, style, width }.
// onExportAll (opcional): async () => ({ title, data }) — mismo shape; no pinta la grilla.
// Si hay onExportAll y más de una página, Excel/CSV abren menú: esta página vs todos.

const NUMERIC_RE = /^-?\d+(?:\.\d+)?$/;
const EXPORT_ALL_CONFIRM_THRESHOLD = 10000;

function toNumberOrNull(v) {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && NUMERIC_RE.test(v.trim())) {
    const n = Number(v.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function flattenPrintRow(item) {
  return item.print.reduce((acc, curr) => {
    if (typeof curr === 'object' && curr !== null) {
      if (curr.print != null && curr.print.toString() !== '[object Object]') {
        acc.push(curr.print);
      }
    } else {
      acc.push(curr);
    }
    return acc;
  }, []);
}

function getColumnMetas(titleRow, numCols) {
  const metas = [];
  for (const item of titleRow || []) {
    const span =
      typeof item === 'object' && item !== null && item.span > 1 ? item.span : 1;
    const meta =
      typeof item === 'object' && item !== null
        ? { type: item.type, decimals: item.decimals }
        : {};
    for (let s = 0; s < span && metas.length < numCols; s++) {
      metas.push(meta);
    }
  }
  while (metas.length < numCols) metas.push({});
  return metas.slice(0, numCols);
}

function getHeaderLabels(titleRow, numCols) {
  const headers = [];
  for (const item of titleRow || []) {
    const label =
      typeof item === 'object' && item !== null
        ? String(
          item.exportLabel
          ?? item.label
          ?? (typeof item.value === 'string' ? item.value : '')
        )
        : String(item ?? '');
    const span =
      typeof item === 'object' && item !== null && item.span > 1 ? item.span : 1;
    for (let s = 0; s < span && headers.length < numCols; s++) {
      headers.push(label);
    }
  }
  while (headers.length < numCols) headers.push('');
  return headers.slice(0, numCols);
}

function countTitleColumns(titleRow) {
  let n = 0;
  for (const item of titleRow || []) {
    const span =
      typeof item === 'object' && item !== null && item.span > 1 ? item.span : 1;
    n += span;
  }
  return n;
}

function buildXlsCell(rawValue, colMeta) {
  const type = colMeta?.type;

  if (type === 'text') {
    return { t: 's', v: rawValue == null ? '' : String(rawValue) };
  }

  const num = toNumberOrNull(rawValue);
  if (num === null) {
    return { t: 's', v: rawValue == null ? '' : String(rawValue) };
  }

  const cell = { t: 'n', v: num };

  if (type === 'currency' || type === 'number') {
    const dec =
      Number.isFinite(colMeta?.decimals) && colMeta.decimals >= 0
        ? colMeta.decimals
        : 2;
    cell.z = `#,##0.${'0'.repeat(dec)}`;
  } else if (type === 'integer') {
    cell.z = '0';
  }
  return cell;
}

function formatCsvNumber(num) {
  return String(num).replace('.', ',');
}

function escapeCsvField(str) {
  const s = str == null ? '' : String(str);
  if (/[;\r\n"]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function formatCsvCell(rawValue, colMeta) {
  const type = colMeta?.type;
  if (type === 'text') {
    return escapeCsvField(rawValue);
  }
  const num = toNumberOrNull(rawValue);
  if (num === null) {
    return escapeCsvField(rawValue);
  }
  return formatCsvNumber(num);
}

function downloadCsvFromGridData(gridData) {
  const title = gridData?.title || [];
  const rows = Array.isArray(gridData?.data) ? gridData.data : [];
  const flatCsvData = rows.map((item) => flattenPrintRow(item));
  const dataNumCols = flatCsvData.reduce((m, row) => Math.max(m, row.length), 0);
  const numCols = Math.max(dataNumCols, countTitleColumns(title));
  const columnMetas = getColumnMetas(title, numCols);
  const headerLabels = getHeaderLabels(title, numCols);

  const csvData = [headerLabels.map(escapeCsvField).join(';')];

  flatCsvData.forEach((row) => {
    const cells = [];
    for (let c = 0; c < numCols; c++) {
      cells.push(formatCsvCell(row[c], columnMetas[c]));
    }
    csvData.push(cells.join(';'));
  });

  const csv = '\uFEFF' + csvData.join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  if (navigator.msSaveBlob) {
    navigator.msSaveBlob(blob, 'Reporte.csv');
  } else {
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'Reporte.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }
}

function downloadXlsFromGridData(gridData) {
  const title = gridData?.title || [];
  const rows = Array.isArray(gridData?.data) ? gridData.data : [];
  const flatRows = rows.map((item) => flattenPrintRow(item));
  const dataNumCols = flatRows.reduce((m, row) => Math.max(m, row.length), 0);
  const numCols = Math.max(dataNumCols, countTitleColumns(title));
  const columnMetas = getColumnMetas(title, numCols);
  const headerLabels = getHeaderLabels(title, numCols);

  const headerCells = headerLabels.map((label) => ({ t: 's', v: label }));
  const dataRows = flatRows.map((row) => {
    const cells = [];
    for (let c = 0; c < numCols; c++) {
      cells.push(buildXlsCell(row[c], columnMetas[c]));
    }
    return cells;
  });

  const aoa = [headerCells, ...dataRows];
  const ws = utils.aoa_to_sheet(aoa);
  const wb = utils.book_new();
  utils.book_append_sheet(wb, ws, 'Reporte');

  const binaryBook = write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([binaryBook], { type: 'application/octet-stream' });
  const fileName = 'Reporte.xlsx';
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = fileName;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

function GridGenerator (props) {

  const baseChoices = props.itemsPerPageChoices ?? [config.defaultItemsPerPage, 5, 50, 100];
  const currentItemsPerPage = props.options?.itemsPerPage;
  const gridConfig = {
    itemsPerPage: [...new Set([...baseChoices, currentItemsPerPage].filter((n) => n != null))].sort((a, b) => a - b),
    defaultItemsPerPage: currentItemsPerPage ?? config.defaultItemsPerPage,
  }

  const [exportMenu, setExportMenu] = useState(null); // 'xls' | 'csv' | null
  const [exporting, setExporting] = useState(false);
  const exportMenuRef = useRef(null);

  const totalRecords = parseInt(props.options?.totalRecords, 10) || 0;
  const itemsPerPage = parseInt(props.options?.itemsPerPage, 10) || gridConfig.defaultItemsPerPage;
  const pageRowCount = Array.isArray(props.data?.data) ? props.data.data.length : 0;
  const hasMultiplePages = totalRecords > itemsPerPage;
  const canExportAll = typeof props.onExportAll === 'function' && hasMultiplePages;

  useEffect(() => {
    if (!exportMenu) return undefined;
    const onDocClick = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setExportMenu(null);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setExportMenu(null);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [exportMenu]);

  const handleSelectChange = (e) => {
    props.handlePages(e, 1, parseInt(e.target.value))
  }

  const handlePageChange = (e) => {

    if (e.target.id === 'grid-page-forward') {

      if(props.options.totalRecords <= ((props.options.page - 1 ) * props.options.itemsPerPage) + props.options.itemsPerPage) return

      props.handlePages(e, parseInt(props.options.page) + 1, parseInt(document.getElementById("grid-select").value) )
  
    } else {
      
      if (parseInt(props.options.page) === 1) return

      props.handlePages(e, parseInt(props.options.page) - 1, parseInt(document.getElementById("grid-select").value) )
    }
  }

  const handlePDF = () => {

    const pdfData = [props.data.title.map((t) =>
      typeof t === 'object' && t !== null
        ? String(t.exportLabel ?? t.label ?? (typeof t.value === 'string' ? t.value : ''))
        : String(t ?? '')
    )]

    const spaceBetween = 10
    const marginTop = 20
    const marginLeft = 20

    const flatCsvData = props.data.data.map((item) => flattenPrintRow(item))

    flatCsvData.forEach( item => {
      pdfData.push(item.map((v) => (v == null ? '' : String(v))))
    })

    const doc = new jsPDF();

    doc.setFont('helvetica')
    doc.setFontSize(6)

    let posX = marginLeft
    let posY = marginTop

    const drawCell = (text, width) => {
      doc.rect(posX, posY, width, spaceBetween ,'S')
      doc.text(posX + 1, posY + spaceBetween / 2, text)
      posX += width
    }

    pdfData.forEach( item => {
      posY += spaceBetween
      posX = marginLeft
      item.forEach( item => {
        drawCell(item, 30)
      })
    })

    doc.save('Reporte.pdf')

  }

  const runExport = (format, gridData) => {
    if (format === 'csv') downloadCsvFromGridData(gridData);
    else downloadXlsFromGridData(gridData);
  };

  const exportPage = (format) => {
    setExportMenu(null);
    runExport(format, props.data);
  };

  const exportAll = async (format) => {
    setExportMenu(null);
    if (typeof props.onExportAll !== 'function' || exporting) return;

    if (totalRecords > EXPORT_ALL_CONFIRM_THRESHOLD) {
      const ok = window.confirm(
        `Son ${totalRecords} registros; la descarga puede tardar. ¿Continuar?`
      );
      if (!ok) return;
    }

    setExporting(true);
    try {
      const result = await props.onExportAll();
      if (result && Array.isArray(result.data)) {
        runExport(format, result);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  const handleExportClick = (format) => {
    if (exporting) return;
    if (canExportAll) {
      setExportMenu((prev) => (prev === format ? null : format));
      return;
    }
    exportPage(format);
  };

  return(
    <div className = "grid-container">
      {props.options &&
        <div className="grid-options">
         
          <div className="grid-options-body">
          {
            props.options.download &&
            <div className="grid-download-icons" ref={exportMenuRef}>
              {exporting ? (
                <span className="grid-exporting" title="Exportando…">
                  <img src={imgLoading} alt="Exportando" height="18px" width="18px" />
                  <span>Exportando…</span>
                </span>
              ) : (
                <>
                  <img
                    src={imgExcel}
                    onClick={() => handleExportClick('xls')}
                    alt="Excel"
                    height="20px"
                    width="20px"
                    title="Descargar Excel"
                  />
                  <img
                    onClick={() => handleExportClick('csv')}
                    src={imgCsv}
                    alt="csv"
                    height="20px"
                    width="20px"
                    title="Descargar CSV"
                  />
                  <img onClick={handlePDF} src={imgPdf} alt="pdf" height="20px" width="20px" title="Descargar PDF (página actual)" />
                  {exportMenu && (
                    <div className="grid-export-dropdown" role="menu">
                      <button
                        type="button"
                        className="grid-export-menu-item"
                        role="menuitem"
                        onClick={() => exportPage(exportMenu)}
                      >
                        {`Esta página (${pageRowCount})`}
                      </button>
                      <button
                        type="button"
                        className="grid-export-menu-item"
                        role="menuitem"
                        onClick={() => exportAll(exportMenu)}
                      >
                        {`Todos los resultados (${totalRecords})`}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          }
          { props.options.page &&
          <div className = "grid-handle-pages">
            <select onChange = {handleSelectChange} id= "grid-select" value={props.options.itemsPerPage}>
              {gridConfig.itemsPerPage.map( item => {
                return <option key = {item} value = {item}>{item}</option>
              })}
            </select>
            <p>{`
            ${((props.options.page - 1 ) * props.options.itemsPerPage) + 1} - 
            ${
              parseInt(props.options.totalRecords) > ((props.options.page - 1 ) * props.options.itemsPerPage) + props.options.itemsPerPage ? ((props.options.page - 1 ) * props.options.itemsPerPage) + props.options.itemsPerPage : props.options.totalRecords
            }
             de ${props.options.totalRecords}`}</p>
            <img 
              src= {imgBack}
              onClick = {handlePageChange} 
              id= "grid-page-back" 
              width = "35px"
              height = "25px"
              alt= "back"
            /> 
            <img 
              src= {imgForward}
              onClick = {handlePageChange} 
              id= "grid-page-forward" 
              width = "35px"
              height = "25px"
              alt= "forward"
            /> 
          </div>
          }
          </div>
          {
            props.options.title &&
            <h3>{props.options.title}</h3>
          }
        </div>
        
      }
    <table className = "grid-class">
      <thead  className = "thead-grid-class">
        <tr>
          {props.data &&
            props.data.title.map((item, index) => {
              if (typeof item == 'object'){
                return <th  className = "th-grid-class" colSpan = {item.span} key = {index}>{item.value}</th>
              } else {
                return <th  className = "th-grid-class" key = {index}>{item}</th>
              }
            })
          }
          {props.actions && <th className = "th-grid-class">Acciones</th>}
        </tr>
      </thead>
      <tbody className = "tbody-grid-class">
        { props.data &&
          props.data.data.map((item) => {
            return <GridColumn
                  data = {item}
                  key = {item.id}
                  rowClick = {props.rowClick}
                  actions = {props.actions}
                />
            })
          }
        </tbody>
     </table>
    </div>
  )
}

export default GridGenerator
